/* =====================================================================
   Wheelie Coach - firmware logic, ported to JS, packaged as a Shopify section.
   ---------------------------------------------------------------------
   This is a PORT, not an imitation. Constants and rules must stay in sync
   with firmware/wheelie_coach/wheelie_coach.ino and web/index.html in the
   wheelie-coach repo. If you change one, change all three, and say so in
   the commit message.

   Scoped per-instance (data-wc-el lookups within the root element)
   instead of document.getElementById, so the section can be dropped onto
   a page more than once without id collisions.
   ===================================================================== */
(function () {
  'use strict';

  var K = {
    BAND_LOW: 18.0, BAND_HIGH: 45.0,
    EQ_RATE: 6.0, EQ_DWELL_MS: 400, BETA: 0.15,
    CONF_MIN: 3, CONF_MAX: 20,
    LOOKAHEAD: 0.30, LOOPOUT_MARGIN: 8.0, DEFAULT_LOOPOUT: 35.0,
    ALARM_CLEAR: 6.0, ALARM_MIN_MS: 400, ALARM_CONFIRM_MS: 60, ALARM_MAX_MS: 15000,
    RATE_PRED_FC: 20.0,
    POCKET_BAND: 3.0, ORANGE_BAND: 1.0,
    TICK_SLOW: 600, TICK_FAST: 90, TICK_LEN: 12,
    HB_PERIOD: 1000, HB_PULSE: 35, HB_PULSE2: 90, HB_GAP: 140,
    IDLE_TIMEOUT_MS: 20000,
    GATE_RATE: 40.0
  };

  /** @type {Record<string, string>} */
  var COLOR = { IDLE: '#9aa3b2', READY: '#5b8def', SEEKING: '#f5a524', POCKET: '#31c48d', ALARM: '#f0484a' };
  /** @type {Record<string, string>} */
  var CUE = {
    IDLE: 'Idle. Move the pitch slider to wake it.',
    READY: 'Rolling. Nothing beeps until you start lifting.',
    SEEKING: 'Ticking — and speeding up as you get closer.',
    POCKET: 'Heartbeat. This is the pocket. Hold exactly this.',
    ALARM: 'ALARM. Predicted loop-out — feather the rear brake.'
  };

  function createSim() {
    var lastNow = -1;
    var st = {
      pitch: 0, rate: 0, ratePred: 0, gated: false,
      balance: 0, learned: false, confidence: 0,
      inWindow: false, winStart: 0, winSum: 0, winN: 0,
      state: 'IDLE', stateSince: 0, alarmSince: 0, lastMotion: 0,
      breachSince: 0, tickPhase: 0, alarmCapped: false
    };

    function projected() { return st.pitch + K.LOOKAHEAD * st.ratePred; }

    /* Predictor rate filter - same first-order, dt-aware low-pass as
       estimator_update() in the .ino. Learner keeps the raw rate.
       @param {number} dt */
    function filterRate(dt) {
      var rc = 1 / (2 * Math.PI * K.RATE_PRED_FC);
      st.ratePred += (dt / (dt + rc)) * (st.rate - st.ratePred);
    }
    function limit() { return st.learned ? st.balance + K.LOOPOUT_MARGIN : K.DEFAULT_LOOPOUT; }
    function trusted() { return st.learned && st.confidence >= K.CONF_MIN; }

    /** @param {number} now */
    function learnerUpdate(now) {
      var p = st.pitch;
      // Only while riding a wheelie (same rule as learner_update()). ALARM
      // counts: an improving rider holds above the old limit with the alarm on.
      // The firmware also discards windows where the bike went stationary in
      // ALARM (a crashed bike); the simulator has no stillness to model.
      var riding = st.state === 'SEEKING' || st.state === 'POCKET' || st.state === 'ALARM';
      var q = riding && p > K.BAND_LOW && p < K.BAND_HIGH && Math.abs(st.rate) < K.EQ_RATE;
      if (q) {
        if (!st.inWindow) { st.inWindow = true; st.winStart = now; st.winSum = 0; st.winN = 0; }
        st.winSum += p; st.winN++;
        return;
      }
      if (st.inWindow) {
        var held = now - st.winStart;
        if (held >= K.EQ_DWELL_MS && st.winN > 0) {
          var pw = st.winSum / st.winN;
          if (!st.learned) { st.balance = pw; st.learned = true; }
          else { st.balance = (1 - K.BETA) * st.balance + K.BETA * pw; }
          if (st.confidence < K.CONF_MAX) st.confidence++;
        }
        st.inWindow = false;
      }
    }

    /** @param {string} s @param {number} now */
    function enter(s, now) {
      if (s === st.state) return;
      st.state = s; st.stateSince = now;
      if (s === 'ALARM') st.alarmSince = now;
    }

    /** @param {number} now */
    function stateUpdate(now) {
      var p = st.pitch, proj = projected(), lim = limit();
      // Capped post-crash alarm: silent IDLE until pitch is back below the
      // wheelie band (FMEA #21, ALARM_MAX_MS in the .ino).
      if (st.alarmCapped) {
        if (p >= K.BAND_LOW) { st.breachSince = 0; enter('IDLE', now); return; }
        st.alarmCapped = false;
      }
      var breached = proj > lim && st.ratePred > 0;
      if (breached) { if (st.breachSince === 0) st.breachSince = now; }
      else { st.breachSince = 0; }

      if (st.state !== 'ALARM' && breached &&
          (now - st.breachSince) >= K.ALARM_CONFIRM_MS) { enter('ALARM', now); return; }

      if (st.state === 'ALARM') {
        if (now - st.alarmSince >= K.ALARM_MAX_MS) {
          st.alarmCapped = true; st.breachSince = 0; st.inWindow = false; enter('IDLE', now); return;
        }
        if (proj < lim - K.ALARM_CLEAR && (now - st.alarmSince) >= K.ALARM_MIN_MS) {
          enter(p > K.BAND_LOW ? 'SEEKING' : 'READY', now);
        }
        return;
      }
      if (now - st.lastMotion > K.IDLE_TIMEOUT_MS) { enter('IDLE', now); return; }
      if (p < K.BAND_LOW) { enter('READY', now); return; }
      if (trusted() && Math.abs(p - st.balance) <= K.POCKET_BAND) enter('POCKET', now);
      else enter('SEEKING', now);
    }

    function tickPeriod() {
      var top = trusted() ? st.balance : K.DEFAULT_LOOPOUT;
      var span = top - K.BAND_LOW;
      var prog = span > 1 ? (st.pitch - K.BAND_LOW) / span : 0;
      prog = Math.max(0, Math.min(1, prog));
      return K.TICK_SLOW - prog * (K.TICK_SLOW - K.TICK_FAST);
    }

    /** @param {number} now */
    function buzzerOn(now) {
      switch (st.state) {
        case 'ALARM': return true;
        case 'POCKET': {                   // asymmetric "lub-DUB"
          var t = now % K.HB_PERIOD;
          return (t < K.HB_PULSE) || (t >= K.HB_GAP && t < K.HB_GAP + K.HB_PULSE2);
        }
        case 'SEEKING': {
          var per = tickPeriod();
          if (now - st.tickPhase >= per) st.tickPhase = now;
          return (now - st.tickPhase) < K.TICK_LEN;
        }
        default: return false;
      }
    }

    function reset() {
      lastNow = -1;
      st.balance = 0; st.learned = false; st.confidence = 0;
      st.inWindow = false; st.winN = 0; st.winSum = 0;
      st.state = 'IDLE'; st.tickPhase = 0; st.breachSince = 0; st.alarmCapped = false;
      st.rate = 0; st.ratePred = 0;
    }

    return {
      K: K, st: st, projected: projected, limit: limit, trusted: trusted,
      buzzerOn: buzzerOn, tickPeriod: tickPeriod, reset: reset,
      /** @param {number} p @param {number} now */
      setPitch: function (p, now) {
        var dt = (lastNow >= 0 && now > lastNow) ? (now - lastNow) / 1000 : 0.05;
        if (dt > 0.5) dt = 0.5;
        lastNow = now;
        st.rate = (p - st.pitch) / dt;
        filterRate(dt);
        st.gated = Math.abs(st.rate) > K.GATE_RATE;
        st.pitch = p;
        st.lastMotion = now;
        learnerUpdate(now);
        stateUpdate(now);
      }
    };
  }

  // ---- gauge geometry: 240 deg sweep, 0..80 deg of pitch ------------
  var A0 = 150, A1 = 390;
  /** @param {number} cx @param {number} cy @param {number} r @param {number} a
      @returns {[number, number]} */
  function pol(cx, cy, r, a) {
    var rad = (a - 90) * Math.PI / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  }
  /** @param {number} cx @param {number} cy @param {number} r @param {number} a0 @param {number} a1 */
  function arc(cx, cy, r, a0, a1) {
    var p0 = pol(cx, cy, r, a0), p1 = pol(cx, cy, r, a1);
    var large = (a1 - a0) > 180 ? 1 : 0;
    return 'M' + p0[0].toFixed(1) + ' ' + p0[1].toFixed(1) +
           ' A' + r + ' ' + r + ' 0 ' + large + ' 1 ' + p1[0].toFixed(1) + ' ' + p1[1].toFixed(1);
  }
  /** @param {number} p */
  function ang(p) { return A0 + (Math.max(0, Math.min(80, p)) / 80) * (A1 - A0); }

  /** @param {Element} root */
  function initInstance(root) {
    var api = createSim();
    var st = api.st, K = api.K;

    // Loosely typed on purpose: the section markup guarantees each element
    // exists once `pitch` does (checked below), and they mix input/SVG/HTML.
    /** @param {string} name @returns {any} */
    function $(name) { return root.querySelector('[data-wc-el="' + name + '"]'); }
    var els = {
      pitch: $('pitch'), look: $('look'),
      lPitch: $('l-pitch'), lLook: $('l-look'),
      pill: $('pill'), cue: $('cue'),
      vPitch: $('v-pitch'), vRate: $('v-rate'), vProj: $('v-proj'),
      vLimit: $('v-limit'), vGate: $('v-gate'), vBal: $('v-bal'), vConf: $('v-conf'),
      arcbg: $('arcbg'), arcYellow: $('arc-yellow'), arcGreen: $('arc-green'),
      arcOrange: $('arc-orange'), arcRed: $('arc-red'),
      needle: $('needle'),
      gnum: $('gnum'), gsub: $('gsub'), glamp: $('glamp'),
      bSound: $('b-sound'), bAuto: $('b-auto'), bReset: $('b-reset')
    };
    if (!els.pitch) return;

    els.arcbg.setAttribute('d', arc(120, 120, 96, A0, A1));

    /** @type {AudioContext | null} */
    var ac = null;
    /** @type {GainNode | null} */
    var gain = null;
    var soundOn = false;
    function initAudio() {
      /** @type {typeof AudioContext | undefined} */
      var Ctx = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
      if (!Ctx) return false;
      ac = new Ctx();
      var osc = ac.createOscillator();
      gain = ac.createGain();
      osc.type = 'square'; osc.frequency.value = 2700;
      gain.gain.value = 0;
      osc.connect(gain); gain.connect(ac.destination);
      osc.start();
      return true;
    }
    els.bSound.addEventListener('click', function () {
      if (!ac && !initAudio()) { els.bSound.textContent = 'Sound unavailable'; return; }
      soundOn = !soundOn;
      if (ac && ac.state === 'suspended' && ac.resume) ac.resume();
      els.bSound.textContent = soundOn ? 'Mute' : 'Enable sound';
    });

    /** @type {((now: number) => number) | null} */
    var auto = null;
    /** @type {number | null} */
    var rafId = null;
    /** @param {number} ts */
    function frame(ts) {
      var now = Math.round(ts);
      var p = parseFloat(els.pitch.value);

      if (auto) {
        p = auto(now);
        els.pitch.value = p.toFixed(1);
      }
      K.LOOKAHEAD = parseFloat(els.look.value);

      api.setPitch(p, now);

      var on = api.buzzerOn(now);
      if (gain) gain.gain.value = (soundOn && on) ? 0.06 : 0;

      var c = COLOR[st.state] || '#9aa3b2';
      // Visual-only cushion: same POCKET state, same heartbeat audio, just a
      // repaint of the top slice of the green zone as a heads-up before ALARM.
      var nearTop = st.state === 'POCKET' && api.trusted() &&
                    st.pitch >= st.balance + K.POCKET_BAND - K.ORANGE_BAND;
      if (nearTop) c = '#f2790a';
      els.gnum.textContent = st.pitch.toFixed(0) + '°';
      els.gnum.setAttribute('fill', st.state === 'ALARM' ? '#f0484a' : '#e9ecf2');
      els.gsub.textContent = st.gated ? 'GYRO ONLY' : 'PITCH';
      els.glamp.setAttribute('fill', on ? c : '#2a2d38');

      /* Static colour-zone gauge face, redrawn every frame since the learned
         balance point (and therefore the zone boundaries) moves over time:
         yellow = below the balance point (still climbing), green = the
         learned pocket, orange = the top slice of that same pocket (a visual
         cushion before red, not a new zone - red still starts exactly where
         it always did), red = past it toward loop-out. Mirrors the
         SEEKING/POCKET/ALARM firmware states but shows the boundaries up
         front instead of only reacting once you're in a state. */
      var lim = api.limit();
      var yellowEnd, orangeEnd, greenEnd;
      if (api.trusted()) {
        yellowEnd = st.balance - K.POCKET_BAND;
        orangeEnd = st.balance + K.POCKET_BAND;
        greenEnd = orangeEnd - K.ORANGE_BAND;
      } else {
        yellowEnd = lim;
        orangeEnd = lim;
        greenEnd = lim;
      }
      yellowEnd = Math.max(0, Math.min(80, yellowEnd));
      greenEnd = Math.max(yellowEnd, Math.min(80, greenEnd));
      orangeEnd = Math.max(greenEnd, Math.min(80, orangeEnd));

      els.arcYellow.setAttribute('d', arc(120, 120, 96, A0, ang(yellowEnd)));
      els.arcGreen.setAttribute('d', greenEnd > yellowEnd ? arc(120, 120, 96, ang(yellowEnd), ang(greenEnd)) : '');
      els.arcOrange.setAttribute('d', orangeEnd > greenEnd ? arc(120, 120, 96, ang(greenEnd), ang(orangeEnd)) : '');
      els.arcRed.setAttribute('d', arc(120, 120, 96, ang(orangeEnd), A1));

      // needle: current pitch, drawn last so it sits on top of the zones
      var na = ang(st.pitch);
      var n0 = pol(120, 120, 20, na), n1 = pol(120, 120, 100, na);
      els.needle.setAttribute('x1', n0[0].toFixed(1)); els.needle.setAttribute('y1', n0[1].toFixed(1));
      els.needle.setAttribute('x2', n1[0].toFixed(1)); els.needle.setAttribute('y2', n1[1].toFixed(1));
      els.needle.setAttribute('stroke', st.state === 'ALARM' ? '#ffffff' : '#e9ecf2');

      els.pill.textContent = st.state;
      els.pill.className = 'wc-statepill wc-st-' + st.state;
      if (nearTop) {
        els.pill.style.color = c; els.pill.style.borderColor = c;
        els.pill.style.background = 'rgba(242,121,10,.10)';
      } else {
        els.pill.style.color = ''; els.pill.style.borderColor = ''; els.pill.style.background = '';
      }
      els.cue.textContent = CUE[st.state] || '';
      els.lPitch.textContent = st.pitch.toFixed(1) + '°';
      els.lLook.textContent = K.LOOKAHEAD.toFixed(2) + ' s';
      els.vPitch.textContent = st.pitch.toFixed(1) + '°';
      els.vRate.textContent = st.rate.toFixed(1) + '°/s';
      els.vProj.textContent = api.projected().toFixed(1) + '°';
      els.vLimit.textContent = api.limit().toFixed(1) + '°';
      els.vGate.textContent = st.gated ? 'GATED (gyro only)' : 'fused';
      els.vBal.textContent = api.trusted() ? st.balance.toFixed(1) + '°' : '—';
      els.vConf.textContent = Math.min(st.confidence, K.CONF_MIN) + ' / ' + K.CONF_MIN;

      rafId = requestAnimationFrame(frame);
    }

    function startAuto() {
      if (auto) return;
      /** @type {number | null} */
      var t0 = null;
      els.bAuto.textContent = 'Stop';
      /** @param {number} now */
      auto = function (now) {
        if (t0 === null) t0 = now;
        var t = (now - t0) / 1000;
        if (t < 0.6) return 2;
        if (t < 1.8) return 2 + (t - 0.6) / 1.2 * 26;
        if (t < 4.2) return 28 + Math.sin((t - 1.8) * 3) * 1.6;
        if (t < 5.4) return 28 + (t - 4.2) / 1.2 * 20;
        if (t < 6.6) return 48 - (t - 5.4) / 1.2 * 46;
        t0 = now;
        return 2;
      };
    }
    function stopAuto() {
      auto = null;
      els.bAuto.textContent = 'Play a wheelie';
    }

    // Auto-play while the simulator is on screen, until the visitor takes
    // control (any button or the pitch slider). Skipped for reduced motion.
    var userTookControl = false;
    function takeControl() {
      userTookControl = true;
      if (observer) { observer.disconnect(); observer = null; }
    }

    els.bAuto.addEventListener('click', function () {
      takeControl();
      if (auto) stopAuto(); else startAuto();
    });

    els.pitch.addEventListener('input', function () {
      takeControl();
      if (auto) stopAuto();
    });

    els.bReset.addEventListener('click', function () {
      takeControl();
      api.reset();
      els.pitch.value = 0;
    });

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /** @type {IntersectionObserver | null} */
    var observer = null;
    if (!reduceMotion && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (userTookControl) return;
        var e = entries[entries.length - 1];
        if (!e) return;
        if (e.isIntersecting) startAuto(); else stopAuto();
      }, { threshold: 0.4 });
      observer.observe(root);
    }

    rafId = requestAnimationFrame(frame);

    // Theme editor: stop the rAF loop if the section is removed/re-rendered,
    // so repeated edits in the customizer don't pile up dangling loops.
    document.addEventListener('shopify:section:unload', function handler(e) {
      var sectionId = /** @type {CustomEvent} */ (e).detail.sectionId;
      if (!root.closest('[data-section-id="' + sectionId + '"]')) return;
      if (rafId) cancelAnimationFrame(rafId);
      if (observer) observer.disconnect();
      document.removeEventListener('shopify:section:unload', handler);
    });
  }

  function init() {
    var roots = document.querySelectorAll('.wc-sim[data-wc]');
    for (var i = 0; i < roots.length; i++) initInstance(/** @type {Element} */ (roots[i]));
  }

  // Exported for the headless smoke test (node tools/smoke_test.js --shopify).
  if (typeof module !== 'undefined' && module.exports) module.exports = createSim;
  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
